import {
  ActionIcon,
  Flex,
  FocusTrap,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  useDebouncedCallback,
} from '@mantine/hooks';
import {
  openModal,
} from '@mantine/modals';
import AddIcon from '@mui/icons-material/Add';
import {
  useForm,
} from '@tanstack/react-form';
import {
  useQueryClient,
} from '@tanstack/react-query';
import {
  createFileRoute,
} from '@tanstack/react-router';
import {
  useMemo,
  useState,
} from 'react';

import {
  SearchInput,
} from '#components/ui/input/SearchInput';
import AddUserModal from '#features/scout/DigitalAssistants/Settings/AddUserModal';
import {
  UserDetailModal,
} from '#features/scout/DigitalAssistants/Settings/UserDetailModal';
import styles from '#features/scout/DigitalAssistants/Settings/Users.module.scss';

export interface UserProps {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

export const Route = createFileRoute(
  '/_app/_authenticated/mydigitalworkers/scout/_scoutLayout/settings/users',
)({
  beforeLoad: () => {
    return {
      crumb: 'Users',
    };
  },
  loader: async (opts) => {
    const usersResult: any[] = await opts.context.queryClient.ensureQueryData({
      queryKey: [
        'reporter',
        'users',
      ],
    });
    const availableResult: any[] = await opts.context.queryClient.ensureQueryData({
      queryKey: [
        'reporter',
        'users',
        'available',
      ],
    });
    const activeUser = opts.context.auth.user;
    const users = usersResult.filter(item => item.email !== activeUser?.profile.email);
    const available = availableResult.filter(item => item.email !== activeUser?.profile.email);
    return {
      userData: users,
      availableUserData: available,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const {
    userData,
    availableUserData,
  } = Route.useLoaderData();
  const queryClient = useQueryClient();
  function refreshHandler() {
    queryClient.invalidateQueries({
      queryKey: [
        'reporter',
        'users',
      ],
    });
  }
  function openModalHandler(userProps: UserProps) {
    openModal({
      closeOnClickOutside: true,
      withCloseButton: true,
      withinPortal: true,
      title: (
        <Text
          c="light.2"
          size="xl"
        >
          Add User
        </Text>
      ),
      children: (
        <>
          <FocusTrap.InitialFocus />
          <UserDetailModal
            show={ userProps }
            onClose={ () => {} }
            user={ currentUser }
            onRefresh={ refreshHandler }
          />
        </>
      ),
    });
  }
  const userValues = useMemo(() => {
    return userData.map(entry => [...Object.values(entry ?? {}).filter(item => typeof item === 'string')].filter(entry => entry ? entry : false),
    ) as string[][];
  }, [userData]);
  const [
    filteredUsers,
    setFilteredUsers,
  ] = useState(userData);
  const handleSearch = useDebouncedCallback(async (formData) => {
    const searchTerm = formData.value.searchTerm;
    const filterResult = userData.filter((_entry: Request, entryIndex: keyof (typeof userValues)) => {
      const entryProperties = userValues[entryIndex] as string[];
      console.debug(entryProperties);
      return entryProperties.some((val) => {
        return (
          typeof val === 'string'
          && val
            .toLowerCase()
            .includes(
              searchTerm.toLowerCase(),
            )
        );
      });
    });
    console.debug(
      `Search term ${searchTerm}, matches ${filterResult.length} / ${userData.length}`,
      userData,
    );
    setFilteredUsers(filterResult);
  }, 250);
  const {
    Field,
    handleSubmit,
    state: _state,
  } = useForm({
    defaultValues: {
      searchTerm: '',
    },
    onSubmit: async (formData) => {
      handleSearch(formData);
    },
  });
  const tableData = {
    // caption: 'Client reports requested',
    head: [
      'NAME',
      '',
      'EMAIL ADDRESS',
      'ROLE',
    ],
    body: filteredUsers.map((user: UserProps) => {
      return {
        id: user.id,
        columns: [
          user.firstName,
          user.lastName,
          user.email,
          (
            user.role === 'client-admin'
              ? 'Global Admin'
              : (user.role === 'scout_admin' ? 'Scout Admin' : 'Scout User')
          ),
        ],
      };
    }),
  };
  const tableRows = tableData.body.map((row, rowIndex) => (
    <Table.Tr
      key={ rowIndex }
      className={ styles.tableRow }
      onClick={ () => openModalHandler({
        id: row.id,
        firstName: row.columns[0],
        lastName: row.columns[1],
        email: row.columns[2],
        role: row.columns[3],
      }) }
    >
      { row.columns.map((cell, cellIndex) => (
        <Table.Td
          key={ cellIndex }
        >
          { cell }
        </Table.Td>
      )) }
    </Table.Tr>
  ));
  const tableHeader = (rowKey: string) => (
    <Table.Tr
      key={ rowKey }
    >
      { tableData.head.map((col, colIndex) => (
        <Table.Td
          key={ colIndex }
        >
          { col }
        </Table.Td>
      )) }
    </Table.Tr>
  );
  return (
    <>
      <Flex
        id="users-table-search"
        direction="row"
        align="center"
        justify="space-between"
        mb="xl"
      >
        <form
          id="users-search-bar"
          onSubmit={ (e) => {
            e.preventDefault();
            // handleSubmit()a
          } }
          onChange={ (e) => {
            e.preventDefault();
            handleSubmit();
          } }
        >
          <Field
            name="searchTerm"
            children={ ({
              state,
              handleChange,
              handleBlur,
            }) => (
              <SearchInput
                data={ [] }
                onChange={ (evt: string): void => handleChange(evt) }
                onBlur={ handleBlur }
                value={ state.value }
              />
            ) }
          />
        </form>
        <Tooltip
          id="tooltip-submit"
          label="Create Request"
          position="left"
          arrowPosition="side"
          arrowOffset={ 2 }
          arrowSize={ 12 }
          withArrow
        >
          <ActionIcon
            id="add-user"
            variant="gradient"
            size="xl"
            radius="xl"
            aria-label="Add User"
            gradient={ {
              from: 'transparent.1',
              to: 'transparent.2',
              deg: 30,
            } }
            onClick={ () => {
              openModal({
                closeOnClickOutside: true,
                withCloseButton: true,
                withinPortal: true,
                title: (
                  <Text
                    c="light.2"
                    size="xl"
                  >
                    Add User
                  </Text>
                ),
                children: (
                  <>
                    <FocusTrap.InitialFocus />
                    <AddUserModal
                      availableUsers={ availableUserData }
                      onRefresh={ refreshHandler }
                    />
                  </>
                ),
              });
            } }
          >
            <AddIcon />
          </ActionIcon>
        </Tooltip>
      </Flex>
      <Table.ScrollContainer
        className={ styles.tableContainer }
        type="native"
        minWidth={ 500 }
      >
        <Table
          classNames={ {} }
          verticalSpacing="sm"
          horizontalSpacing="lg"
          striped="odd"
          stripedColor="oklch(1 0 0 /0.05)"
          highlightOnHoverColor="primary.9"
          highlightOnHover
          stickyHeader
          tabularNums
        >
          <Table.Thead>{ tableHeader('users-table-header') }</Table.Thead>
          <Table.Tbody>{ tableRows }</Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {/* { currentUser
      && (
        <UserDetailModal
          show={ currentUser }
          onClose={ closeDetailModalHandler }
          user={ currentUser }
          onRefresh={ refreshHandler }
        />
      ) } */}
    </>
  );
}
